import { Node, Edge } from "@xyflow/react";
import { StepCreateRequest } from "api/main/data-contracts";
import { StepNodeData } from "../components/StepNode";

const STEP_VERTICAL_SPACING = 250;
const STEP_HORIZONTAL_CENTER = 400;

/**
 * Convert Formik steps array to React Flow nodes and edges
 */
export function stepsToFlow(
  steps: StepCreateRequest[],
  onEdit: (stepIndex: number) => void,
  onDelete: (stepIndex: number) => void
): { nodes: Node<StepNodeData>[]; edges: Edge[] } {
  const nodes: Node<StepNodeData>[] = steps.map((step, index) => ({
    id: `step-${index}`,
    type: "stepNode",
    position: {
      x: STEP_HORIZONTAL_CENTER,
      y: index * STEP_VERTICAL_SPACING,
    },
    data: {
      stepIndex: index,
      stepType: step.type || "",
      stepName: step.name || "",
      executors: step.executors || [],
      notificationType: step.notificationType,
      onEdit,
      onDelete,
    },
  }));

  const edges: Edge[] = [];
  for (let i = 0; i < steps.length - 1; i++) {
    edges.push({
      id: `edge-${i}-${i + 1}`,
      source: `step-${i}`,
      target: `step-${i + 1}`,
      type: "smoothstep",
      animated: true,
      style: { stroke: "#1976d2", strokeWidth: 2 },
    });
  }

  return { nodes, edges };
}

/**
 * Update a specific node's data in the nodes array
 */
export function updateNodeData(
  nodes: Node<StepNodeData>[],
  stepIndex: number,
  step: StepCreateRequest
): Node<StepNodeData>[] {
  return nodes.map((node) => {
    if (node.data.stepIndex === stepIndex) {
      return {
        ...node,
        data: {
          ...node.data,
          stepType: step.type || "",
          stepName: step.name || "",
          executors: step.executors || [],
          notificationType: step.notificationType,
        },
      };
    }
    return node;
  });
}

/**
 * Auto-layout algorithm: arrange nodes in a vertical flow
 */
export function autoLayoutNodes<T extends Record<string, unknown>>(nodes: Node<T>[]): Node<T>[] {
  return nodes.map((node, index) => ({
    ...node,
    position: {
      x: STEP_HORIZONTAL_CENTER,
      y: index * STEP_VERTICAL_SPACING,
    },
  }));
}

/**
 * Reorder nodes after drag operation
 * This updates the stepIndex in node data based on vertical position
 */
export function reorderNodesByPosition(nodes: Node<StepNodeData>[]): Node<StepNodeData>[] {
  // Sort nodes by Y position
  const sortedNodes = [...nodes].sort((a, b) => a.position.y - b.position.y);

  // Update stepIndex to match new order
  return sortedNodes.map((node, index) => ({
    ...node,
    data: {
      ...node.data,
      stepIndex: index,
    },
  }));
}

/**
 * Rebuild edges based on node order
 */
export function rebuildEdges(nodes: Node<StepNodeData>[]): Edge[] {
  const sortedNodes = [...nodes].sort(
    (a, b) => a.data.stepIndex - b.data.stepIndex
  );

  const edges: Edge[] = [];
  for (let i = 0; i < sortedNodes.length - 1; i++) {
    edges.push({
      id: `edge-${sortedNodes[i].id}-${sortedNodes[i + 1].id}`,
      source: sortedNodes[i].id,
      target: sortedNodes[i + 1].id,
      type: "smoothstep",
      animated: true,
      style: { stroke: "#1976d2", strokeWidth: 2 },
    });
  }

  return edges;
}

/**
 * Add a new step node at the end of the flow
 */
export function addStepNode(
  nodes: Node<StepNodeData>[],
  stepType: string,
  onEdit: (stepIndex: number) => void,
  onDelete: (stepIndex: number) => void
): Node<StepNodeData> {
  const newIndex = nodes.length;
  const newNode: Node<StepNodeData> = {
    id: `step-${newIndex}`,
    type: "stepNode",
    position: {
      x: STEP_HORIZONTAL_CENTER,
      y: newIndex * STEP_VERTICAL_SPACING,
    },
    data: {
      stepIndex: newIndex,
      stepType: stepType,
      stepName: "",
      executors: [],
      notificationType: "NONE",
      onEdit,
      onDelete,
    },
  };

  return newNode;
}

/**
 * Remove a node from the flow
 */
export function removeNode(
  nodes: Node<StepNodeData>[],
  nodeId: string
): Node<StepNodeData>[] {
  const filteredNodes = nodes.filter((node) => node.id !== nodeId);

  // Reindex the remaining nodes
  return filteredNodes.map((node, index) => ({
    ...node,
    id: `step-${index}`, // Update ID to match new index
    data: {
      ...node.data,
      stepIndex: index,
    },
    position: {
      ...node.position,
      y: index * STEP_VERTICAL_SPACING,
    },
  }));
}

/**
 * Get the step indices in the order they appear in the flow
 */
export function getStepOrder(nodes: Node<StepNodeData>[]): number[] {
  return [...nodes]
    .sort((a, b) => a.position.y - b.position.y)
    .map((node) => node.data.stepIndex);
}
